import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  ArrowLeft,
  Search,
  Filter,
  Download,
  Edit,
  Eye,
  Award,
  TrendingUp,
  BarChart3,
  Users,
  BookOpen,
  Calculator,
  Calendar,
  FileText,
  User,
  CheckCircle,
  Save,
  X,
  BarChart,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useAuth, useNavigation } from "../App";
import { ROLES } from "../constant/roles";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { UserProfileModal } from "../components/UserProfileModal";
import { toast } from "sonner";
import GradeDetailsPage from "./GradeDetailsPage";
import { useQuery } from "@tanstack/react-query";
import {
  getAllGrades,
  ApiGradeItem,
  Project,
  getStudentsGradebook,
  getGradeContexts,
  getGradeProjects,
  StudentGradebookItem,
  StudentsGradebookResponse,
  GradeContext,
  GradeProject,
} from "../api/gradesApi";
import StudentGradebookModal from "../components/StudentGradebookModal";
import { PageHeader, ProjectHeader } from "../components/ui/page-header";
import useDebounce from "../hooks/useDebounce";
import { useNavigate } from "react-router-dom";
import { downloadAsXlsx } from "../helper/helperFunctions";
import paths from "../routes";

// Rubric Interface
interface RubricItem {
  id: string;
  criteria: string;
  description: string;
  points: number;
  earnedPoints?: number;
}

// Utility function to download data as Excel-compatible CSV
const downloadExcel = (data: any[], filename: string) => {
  if (!data || data.length === 0) {
    toast.error("No data available to export");
    return;
  }

  // Add BOM for Excel UTF-8 recognition
  const BOM = "\uFEFF";
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(","),
    ...data.map((row) =>
      headers
        .map((header) => {
          const value = row[header];
          if (
            value &&
            (value.toString().includes(",") ||
              value.toString().includes('"') ||
              value.toString().includes("\n"))
          ) {
            return `"${value.toString().replace(/"/g, '""')}"`;
          }
          return value || "";
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([BOM + csvContent], {
    type: "text/csv;charset=utf-8;",
  });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Helper function to transform grades data for export
function transformGradesForExport(grades: ApiGradeItem[]) {
  return grades.map((grade) => ({
    "Student Name": grade.submission.user.name,
    "Student Email": grade.submission.user.email,
    "Project Title": grade.project.title,
    Course: grade.project.course.name,
    "Course Code": grade.project.course.code,
    "Total Score": grade.totalScore,
    "Max Score": grade.maxScore,
    Percentage: grade.percentage,
    Status: grade.submission.status,
    "Submitted At": new Date(grade.submission.submittedAt).toLocaleString(),
    "Graded At": grade.gradedAt
      ? new Date(grade.gradedAt).toLocaleString()
      : "Not Graded",
    "Total Checkpoints": grade.checkpoints.total,
    "Completed Checkpoints": grade.checkpoints.completed,
    "In Progress": grade.checkpoints.inProgress,
    "Not Started": grade.checkpoints.notStarted,
    Overdue: grade.checkpoints.overdue,
    Feedback: grade.feedback || "No feedback",
    Evaluator: grade.evaluator?.name || "Not Assigned",
  }));
}

// Helper function to transform students data for export
function transformStudentsForExport(students: StudentGradebookItem[]) {
  return students.map((student) => ({
    Name: student.name,
    Email: student.email,
    "Assigned Projects": student.assigned_projects_count,
    "User ID": student.user_id,
  }));
}

// Helper function to map API status to UI status
function mapApiStatusToUIStatus(
  apiStatus: string
): "not_started" | "in_progress" | "submitted" | "graded" {
  switch (apiStatus) {
    case "completed":
    case "graded":
      return "graded";
    case "submitted":
      return "submitted";
    case "grading":
      return "submitted";
    case "in_progress":
      return "in_progress";
    case "revision_needed":
      return "in_progress";
    case "not_started":
      return "not_started";
    default:
      return "not_started";
  }
}

// Helper function to transform API grade data to Project format
function transformApiGradeToProject(apiGrade: ApiGradeItem): Project {
  const checkpoints = apiGrade.checkpoints.details.map((cp) => ({
    id: cp.id,
    title: cp.title,
    description: cp.description,
    status: mapApiStatusToUIStatus(cp.status) as any,
    dueDate: cp.dueDate,
    submittedDate: cp.submittedAt || undefined,
    grade: cp.totalScore?.toString() || undefined,
    instructorFeedback: cp.instructorFeedback
      ? [cp.instructorFeedback]
      : undefined,
    submittedBy: cp?.submittedBy,
    maxScore: cp?.maxScore,
    totalScore: cp?.totalScore,
    feedback: cp?.feedback,
  }));

  return {
    id: apiGrade.project.id,
    trimester: 2,
    title: apiGrade.project.title,
    isGroup: apiGrade.project.isGroup,
    projectType: apiGrade.project.type,
    description: "",
    subject: apiGrade.project.course.name,
    instructor: apiGrade.evaluator?.name || "Not Assigned",
    startDate: null,
    dueDate: apiGrade.project.dueDate,
    createdDate: null,
    status: mapApiStatusToUIStatus(apiGrade.submission.status),
    progress: 0,
    totalPoints: parseFloat(apiGrade.maxScore) || 100,
    difficulty: "Intermediate" as const,
    objectives: [],
    requirements: [],
    datasets: [],
    submissions: [
      {
        id: apiGrade.submission.id,
        submittedAt: apiGrade.submission.submittedAt,
        files: [],
        grade: parseFloat(apiGrade.totalScore),
        feedback: apiGrade.feedback,
        status: mapApiStatusToUIStatus(apiGrade.submission.status) as any,
      },
    ],
    rubric: [],
    estimatedHours: 0,
    tags: [],
    checkpoints,
    _overallScore: parseFloat(apiGrade.totalScore) || undefined,
    _maxScore: parseFloat(apiGrade.maxScore) || undefined,
    _totalCheckpoints: apiGrade.checkpoints.total,
    projectStatus: apiGrade.projectStatus, // Added to pass project status from API
  };
}
function StudentGradebookView() {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<string>("all");
  const [selectedProject, setSelectedProject] = useState<string>("all");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [gradesItem, setGradesItem] = useState<Project[]>([]);
  const [totalItems, setTotalItems] = useState({
    totalPages: 0,
    totalItems: 0,
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, itemsPerPage]);

  // Fetch courses for filter
  const {
    data: contextsData,
    isLoading: isLoadingContexts,
    error: contextsError,
  } = useQuery({
    queryKey: ["gradeContexts"],
    queryFn: getGradeContexts,
    enabled: true,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch projects for filter
  const { data: projectsData, isLoading: isLoadingProjects } = useQuery({
    queryKey: ["gradeProjects", selectedCourse],
    queryFn: () => {
      const params: any = {};
      if (selectedCourse && selectedCourse !== "all")
        params.course = selectedCourse;
      return getGradeProjects(params);
    },
    enabled: true,
    retry: false,
  });

  // Fetch grades from API with filters
  const {
    data: gradesResponse,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [
      "studentGrades",
      selectedCourse,
      selectedProject,
      debouncedSearchTerm,
      currentPage,
      itemsPerPage,
    ],
    queryFn: () => {
      const params: any = {
        page: currentPage,
        limit: itemsPerPage,
        isExport: false,
      };
      if (selectedCourse && selectedCourse !== "all")
        params.courseId = selectedCourse;
      if (selectedProject && selectedProject !== "all")
        params.projectId = selectedProject;
      if (debouncedSearchTerm) params.searchTerm = debouncedSearchTerm;
      return getAllGrades(params);
    },
  });
  useEffect(() => {
    setCurrentPage(gradesResponse?.data?.pagination?.currentPage || 0);
    // setItemsPerPage(gradesResponse?.data?.pagination?.itemsPerPage||0)
    setGradesItem(
      gradesResponse?.data.grades.map(transformApiGradeToProject) || []
    );
    setTotalItems({
      totalPages: gradesResponse?.data?.pagination?.totalPages || 0,
      totalItems: gradesResponse?.data?.pagination?.totalItems || 0,
    });
  }, [gradesResponse]);

  // Transform API data to Project format
  // const projectsData2 = useMemo(() => {
  //   if (!gradesResponse?.data?.grades) return [];
  //   return gradesResponse.data.grades.map(transformApiGradeToProject);
  // }, [gradesResponse]);

  // Export handler for student grades
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const params: any = { isExport: true };
      if (selectedCourse && selectedCourse !== "all")
        params.courseId = selectedCourse;
      if (selectedProject && selectedProject !== "all")
        params.projectId = selectedProject;
      if (debouncedSearchTerm) params.searchTerm = debouncedSearchTerm;

      const exportData = await getAllGrades(params);
      await downloadAsXlsx(
        exportData,
        `grades_export_${new Date().toISOString().split("T")[0]}.xlsx`
      );

      // if (exportData.isSuccess && exportData.data?.grades) {
      //   const transformedData = transformGradesForExport(
      //     exportData.data.grades
      //   );
      //   const timestamp = new Date().toISOString().split("T")[0];
      //   const courseLabel = selectedCourse !== "all" ? `-course` : "";
      //   const projectLabel = selectedProject !== "all" ? `-project` : "";
      //   downloadExcel(
      //     transformedData,
      //     `my-grades${courseLabel}${projectLabel}-${timestamp}.csv`
      //   );
      //   toast.success(
      //     `${transformedData.length} grade${
      //       transformedData.length !== 1 ? "s" : ""
      //     } exported successfully!`
      //   );
      // } else {
      //   toast.error("No data available to export");
      // }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export grades. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  // Helper function to get tooltip text
  const getStatusTooltip = (status: string) => {
    switch (status) {
      case "graded":
        return "Project has been graded and completed";
      case "submitted":
        return "Project has been submitted and is being graded";
      case "in_progress":
        return "Project is currently in progress";
      default:
        return "Project status";
    }
  };

  // Memoize the sorted and filtered projects list
  // const filteredProjects = useMemo(() => {
  //   const sorted = [...projectsData2].sort((a, b) => b.trimester - a.trimester);

  //   if (!searchTerm) {
  //     return sorted;
  //   }
  //   return sorted.filter(
  //     (project) =>
  //       project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //       project.subject.toLowerCase().includes(searchTerm.toLowerCase())
  //   );
  // }, [projectsData2, searchTerm]);

  // const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);

  // const paginatedProjects = useMemo(() => {
  //   const startIndex = (currentPage - 1) * itemsPerPage;
  //   const endIndex = startIndex + itemsPerPage;
  //   return filteredProjects.slice(startIndex, endIndex);
  // }, [filteredProjects, currentPage, itemsPerPage]);
  const handleCourseChange = (value: string) => {
    setCurrentPage(1);
    setSelectedProject("all");
    setSelectedCourse(value);
  };

  // Loading state
  // if (isLoading) {
  //   return (
  //     <div className="space-y-6">
  //       <Card>
  //         <CardHeader>
  //           <CardTitle className="text-xl font-semibold">
  //             Project Grades
  //           </CardTitle>
  //         </CardHeader>
  //         <CardContent>
  //           <div className="flex items-center justify-center py-12">
  //             <div className="text-center space-y-2">
  //               <div className="animate-spin h-8 w-8 border-4 border-bits-blue border-t-transparent rounded-full mx-auto"></div>
  //               <p className="text-muted-foreground">Loading grades...</p>
  //             </div>
  //           </div>
  //         </CardContent>
  //       </Card>
  //     </div>
  //   );
  // }

  // // Error state
  // if (isError) {
  //   return (
  //     <div className="space-y-6">
  //       <Card>
  //         <CardHeader>
  //           <CardTitle className="text-xl font-semibold">
  //             Project Grades
  //           </CardTitle>
  //         </CardHeader>
  //         <CardContent>
  //           <div className="flex items-center justify-center py-12">
  //             <div className="text-center space-y-2">
  //               <p className="text-red-600">Failed to load grades</p>
  //               <p className="text-sm text-muted-foreground">
  //                 {error?.message || "An error occurred"}
  //               </p>
  //             </div>
  //           </div>
  //         </CardContent>
  //       </Card>
  //     </div>
  //   );
  // }

  // // Empty state
  // if (projectsData2.length === 0) {
  //   return (
  //     <div className="space-y-6">
  //       <Card>
  //         <CardHeader>
  //           <div className="flex items-center justify-between">
  //             <CardTitle className="text-xl font-semibold">
  //               Project Grades
  //             </CardTitle>
  //             <div className="flex items-center space-x-2">
  //               <Select
  //                 value={selectedCourse}
  //                 onValueChange={handleCourseChange}
  //               >
  //                 <SelectTrigger className="w-40 bg-white overflow-hidden">
  //                   <SelectValue placeholder="All courses" />
  //                 </SelectTrigger>
  //                 <SelectContent className="max-h-80 overflow-y-auto">
  //                   <SelectItem value="all">All courses</SelectItem>
  //                   {contextsData?.data?.data?.map((context: GradeContext) => (
  //                     <SelectItem key={context.id} value={context.id}>
  //                       {context.contextTitle} ({context.contextLabel})
  //                     </SelectItem>
  //                   ))}
  //                 </SelectContent>
  //               </Select>
  //               <Select
  //                 value={selectedProject}
  //                 onValueChange={setSelectedProject}
  //               >
  //                 <SelectTrigger className="w-40 bg-white overflow-hidden">
  //                   <SelectValue placeholder="All projects" />
  //                 </SelectTrigger>
  //                 <SelectContent className="max-h-80 overflow-y-auto">
  //                   <SelectItem value="all">All projects</SelectItem>
  //                   {projectsData?.data?.projects?.map(
  //                     (project: GradeProject) => (
  //                       <SelectItem key={project.id} value={project.id}>
  //                         {project.title}
  //                       </SelectItem>
  //                     )
  //                   )}
  //                 </SelectContent>
  //               </Select>
  //             </div>
  //           </div>
  //         </CardHeader>
  //         <CardContent>
  //           <div className="flex items-center justify-center py-12">
  //             <div className="text-center space-y-2">
  //               <FileText className="h-12 w-12 text-muted-foreground mx-auto" />
  //               <p className="text-muted-foreground">No grades available yet</p>
  //             </div>
  //           </div>
  //         </CardContent>
  //       </Card>
  //     </div>
  //   );
  // }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-semibold">
              Project Grades
            </CardTitle>
            <div className="flex items-center space-x-2">
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search project, course..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={selectedCourse} onValueChange={handleCourseChange}>
                <SelectTrigger className="w-40 bg-white overflow-hidden">
                  <SelectValue placeholder="All courses" />
                </SelectTrigger>
                <SelectContent className="max-h-80 overflow-y-auto">
                  <SelectItem value="all">All courses</SelectItem>
                  {contextsData?.data?.map((context: GradeContext) => (
                    <SelectItem key={context.id} value={context.id}>
                      {context.name} ({context.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={selectedProject}
                onValueChange={setSelectedProject}
              >
                <SelectTrigger className="w-40 bg-white overflow-hidden">
                  <SelectValue placeholder="All projects" />
                </SelectTrigger>
                <SelectContent className="max-h-80 overflow-y-auto">
                  <SelectItem value="all">All projects</SelectItem>
                  {projectsData?.data?.map((project: GradeProject) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={isExporting}
              >
                <Download className="h-4 w-4 mr-2" />
                {isExporting ? "Exporting..." : "Export Grades"}
              </Button> */}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table className="w-full">
            <TableHeader>
              <TableRow className="text-sm">
                <TableHead className="w-[70px] py-2 px-2 text-xs text-center">
                  Trimester
                </TableHead>
                <TableHead className="w-[200px] py-2 px-2 text-xs">
                  Project
                </TableHead>
                {/* <TableHead className="w-[110px] py-2 px-2 text-xs text-center">
                  Start Date
                </TableHead> */}
                <TableHead className="w-[110px] py-2 px-2 text-xs text-center">
                  End Date
                </TableHead>
                <TableHead className="w-[90px] py-2 px-2 text-xs text-center">
                  Status
                </TableHead>
                <TableHead className="w-[90px] py-2 px-2 text-xs text-center">
                  Score
                </TableHead>
                <TableHead className="w-[90px] py-2 px-2 text-xs text-center">
                  Max
                </TableHead>
                <TableHead className="w-[150px] py-2 px-2 text-xs">
                  Progress
                </TableHead>
                <TableHead className="w-[100px] py-2 px-2 text-xs text-center">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12">
                    <div className="flex items-center justify-center">
                      <div className="text-center space-y-2">
                        <div className="animate-spin h-8 w-8 border-4 border-bits-blue border-t-transparent rounded-full mx-auto"></div>
                        <p className="text-muted-foreground">Loading data...</p>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12">
                    <div className="flex items-center justify-center">
                      <div className="text-center space-y-2">
                        <p className="text-red-600">Failed to load</p>
                        {/* <p className="text-sm text-muted-foreground">
                          {isError?.message || "An error occurred"}
                        </p> */}
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : gradesItem?.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No Grades found
                  </TableCell>
                </TableRow>
              ) : (
                gradesItem?.map((project) => {
                  // Count checkpoints with status "completed" or "graded"
                  const completedCheckpoints =
                    project.checkpoints?.filter(
                      (checkpoint) =>
                        checkpoint.status === "completed" ||
                        checkpoint.status === "graded"
                    ).length || 0;
                  const totalCheckpoints = project._totalCheckpoints || 0;
                  const progressPercentage =
                    totalCheckpoints > 0
                      ? (completedCheckpoints / totalCheckpoints) * 100
                      : 0;

                  return (
                    <TableRow key={project.id}>
                      <TableCell className="w-[70px] text-sm text-center py-2 px-2">
                        {project.trimester}
                      </TableCell>
                      <TableCell className="w-[200px] py-2 px-2">
                        <div className="text-sm font-medium truncate">
                          {project.title}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {project.subject}
                        </div>
                      </TableCell>
                      {/* <TableCell className="w-[110px] text-xs text-center py-2 px-2">
                      {project.startDate
                        ? new Date(project.startDate).toLocaleDateString(
                            "en-GB",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            }
                          )
                        : "-"}
                    </TableCell> */}
                      <TableCell className="w-[110px] text-xs text-center py-2 px-2">
                        {project.dueDate
                          ? new Date(project.dueDate).toLocaleDateString(
                              "en-GB",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              }
                            )
                          : "-"}
                      </TableCell>
                      <TableCell className="w-[90px] py-2 px-2">
                        <div className="flex justify-center">
                          <Badge
                            variant={
                              project.projectStatus === "graded"
                                ? "default"
                                : "secondary"
                            }
                            className={`text-xs capitalize ${
                              project.projectStatus === "Completed"
                                ? "bg-green-100 text-bits-green hover:bg-green-200"
                                : project.projectStatus === "Submitted"
                                ? "bg-blue-100 text-bits-blue hover:bg-blue-200"
                                : "bg-yellow-50 text-bits-warning hover:bg-yellow-100"
                            }`}
                            title={getStatusTooltip(
                              project.projectStatus || ""
                            )}
                          >
                            {project.projectStatus}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="w-[90px] text-sm text-center py-2 px-2">
                        {project._overallScore !== undefined
                          ? project._overallScore.toFixed(1)
                          : "-"}
                      </TableCell>
                      <TableCell className="w-[90px] text-sm text-center py-2 px-2">
                        {project._maxScore !== undefined
                          ? project._maxScore.toFixed(1)
                          : "-"}
                      </TableCell>
                      <TableCell className="w-[150px] py-2 px-2">
                        <div className="flex flex-col space-y-1">
                          <span className="text-xs text-muted-foreground truncate">
                            {`${completedCheckpoints}/${totalCheckpoints} checkpoint${
                              totalCheckpoints !== 1 ? "s" : ""
                            }`}
                          </span>
                          <Progress
                            value={progressPercentage}
                            className="h-1.5"
                          />
                        </div>
                      </TableCell>
                      <TableCell className="w-[100px] text-center py-2 px-2">
                        <GradeDetailsPage project={project} />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
              {/* {gradesItem?.map((project) => {
                const completedSubmissions =
                  project.submissions?.filter(
                    (sub) =>
                      sub.status === "graded" || sub.status === "submitted"
                  ).length || 0;
                const totalCheckpoints = project._totalCheckpoints || 0;
                const progressPercentage =
                  totalCheckpoints > 0
                    ? (completedSubmissions / totalCheckpoints) * 100
                    : 0;

                return (
                  <TableRow key={project.id}>
                    <TableCell className="w-[70px] text-sm text-center py-2 px-2">
                      {project.trimester}
                    </TableCell>
                    <TableCell className="w-[200px] py-2 px-2">
                      <div className="text-sm font-medium truncate">
                        {project.title}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {project.subject}
                      </div>
                    </TableCell>
                    <TableCell className="w-[110px] text-xs text-center py-2 px-2">
                      {project.startDate
                        ? new Date(project.startDate).toLocaleDateString(
                            "en-GB",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            }
                          )
                        : "-"}
                    </TableCell>
                    <TableCell className="w-[110px] text-xs text-center py-2 px-2">
                      {project.dueDate
                        ? new Date(project.dueDate).toLocaleDateString(
                            "en-GB",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            }
                          )
                        : "-"}
                    </TableCell>
                    <TableCell className="w-[90px] py-2 px-2">
                      <div className="flex justify-center">
                        <Badge
                          variant={
                            project.status === "graded"
                              ? "default"
                              : "secondary"
                          }
                          className={`text-xs ${
                            project.status === "graded"
                              ? "bg-green-100 text-bits-green hover:bg-green-200"
                              : project.status === "submitted"
                              ? "bg-blue-100 text-bits-blue hover:bg-blue-200"
                              : "bg-yellow-50 text-bits-warning hover:bg-yellow-100"
                          }`}
                          title={getStatusTooltip(project.status)}
                        >
                          {project.status === "graded"
                            ? "Graded"
                            : project.status === "submitted"
                            ? "Submitted"
                            : "Progress"}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="w-[90px] text-sm text-center py-2 px-2">
                      {project._overallScore !== undefined
                        ? project._overallScore.toFixed(1)
                        : "-"}
                    </TableCell>
                    <TableCell className="w-[90px] text-sm text-center py-2 px-2">
                      {project._maxScore !== undefined
                        ? project._maxScore.toFixed(1)
                        : "-"}
                    </TableCell>
                    <TableCell className="w-[150px] py-2 px-2">
                      <div className="flex flex-col space-y-1">
                        <span className="text-xs text-muted-foreground truncate">
                          {`${completedSubmissions}/${totalCheckpoints} checkpoint${
                            totalCheckpoints !== 1 ? "s" : ""
                          }`}
                        </span>
                        <Progress
                          value={progressPercentage}
                          className="h-1.5"
                        />
                      </div>
                    </TableCell>
                    <TableCell className="w-[100px] text-center py-2 px-2">
                      <GradeDetailsPage project={project} />
                    </TableCell>
                  </TableRow>
                );
              })} */}
            </TableBody>
          </Table>

          {/* New Pagination - Similar to UserManagementPage */}
          {!isLoading && !isError && gradesItem?.length > 0 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-muted-foreground">
                {/* Page {currentPage} of {totalPages} • {filteredProjects.length}{" "}
                items */}
                Page {currentPage} of {totalItems.totalPages} •{" "}
                {totalItems.totalItems} items
              </div>

              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="h-9"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Prev
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage(
                        Math.min(totalItems.totalPages, currentPage + 1)
                      )
                    }
                    disabled={currentPage === totalItems.totalPages}
                    className="h-9"
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>

                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={(value) => {
                    setItemsPerPage(Number(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-[120px] h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 page</SelectItem>
                    <SelectItem value="10">10 page</SelectItem>
                    <SelectItem value="20">20 page</SelectItem>
                    <SelectItem value="50">50 page</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
function AdminGradebookView({
  selectedCourse,
  selectedProject,
}: {
  selectedCourse: string;
  selectedProject: string;
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStudent, setSelectedStudent] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [isExportingStudents, setIsExportingStudents] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCourse, selectedProject, debouncedSearchTerm]);

  const {
    data: studentsData,
    isLoading: isLoadingStudents,
    error: studentsError,
  } = useQuery({
    queryKey: [
      "studentsGradebook",
      currentPage,
      selectedCourse,
      selectedProject,
      debouncedSearchTerm,
      itemsPerPage,
    ],
    queryFn: () => {
      const params: any = {
        page: currentPage,
        limit: itemsPerPage,
      };
      if (selectedCourse && selectedCourse !== "all")
        params.course = selectedCourse;
      if (selectedProject && selectedProject !== "all")
        params.project = selectedProject;
      if (debouncedSearchTerm) params.search = debouncedSearchTerm;
      return getStudentsGradebook(params);
    },
  });

  const {
    data: contextsData,
    isLoading: isLoadingContexts,
    error: contextsError,
  } = useQuery({
    queryKey: ["gradeContexts"],
    queryFn: getGradeContexts,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  const { data: projectsData, isLoading: isLoadingProjects } = useQuery({
    queryKey: ["gradeProjects", selectedCourse],
    queryFn: () => {
      const params: any = {};
      if (selectedCourse && selectedCourse !== "all")
        params.course = selectedCourse;
      return getGradeProjects(params);
    },
  });

  // Export handler for students list
  const handleExportStudents = async () => {
    setIsExportingStudents(true);
    try {
      const allStudents: StudentGradebookItem[] = [];
      let currentExportPage = 1;
      let hasMoreData = true;
      const exportLimit = 100;

      // Fetch all pages
      while (hasMoreData) {
        const params: any = {
          page: currentExportPage,
          limit: exportLimit,
        };
        if (selectedCourse && selectedCourse !== "all")
          params.course = selectedCourse;
        if (selectedProject && selectedProject !== "all")
          params.project = selectedProject;
        if (debouncedSearchTerm) params.search = debouncedSearchTerm;

        const exportData = await getStudentsGradebook(params);

        if (exportData?.data?.data && exportData.data.data.length > 0) {
          allStudents.push(...exportData.data.data);

          const totalPages = exportData.data.pagination?.totalPages || 1;
          if (currentExportPage >= totalPages) {
            hasMoreData = false;
          } else {
            currentExportPage++;
          }
        } else {
          hasMoreData = false;
        }
      }

      if (allStudents.length > 0) {
        const transformedData = transformStudentsForExport(allStudents);
        const timestamp = new Date().toISOString().split("T")[0];
        downloadExcel(transformedData, `students-list-${timestamp}.csv`);
        toast.success(`${allStudents.length} students exported successfully!`);
      } else {
        toast.error("No students data available to export");
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export students list. Please try again.");
    } finally {
      setIsExportingStudents(false);
    }
  };

  const handleViewProfile = (student: StudentGradebookItem) => {
    setSelectedStudent({ id: student.user_id, name: student.name });
    setShowStudentModal(true);
  };

  const handleStudentModalClose = () => {
    setShowStudentModal(false);
    setSelectedStudent(null);
  };

  const [students, setStudents] = useState<StudentGradebookItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    if (!studentsData) return;
    if (studentsData?.data?.data) {
      setStudents(studentsData.data.data);
    } else {
      setStudents([]);
    }

    if (studentsData?.data?.pagination?.totalItems) {
      setTotalItems(studentsData.data.pagination.totalItems);
    } else {
      setTotalItems(0);
    }

    if (studentsData?.data?.pagination?.totalPages) {
      setTotalPages(studentsData.data.pagination.totalPages);
    } else {
      setTotalPages(0);
    }
  }, [studentsData]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-col py-6">
            <CardTitle className="text-2xl font-bold">{totalItems}</CardTitle>
            <div className="text-sm text-bits-grey-600">Total Students</div>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader className="flex flex-col py-6">
            <CardTitle className="text-2xl font-bold">
              {projectsData?.data?.length || 0}
            </CardTitle>
            <div className="text-sm text-bits-grey-600">Total Projects</div>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <div className="flex px-5 py-5 justify-between">
          <h1 className="text-xl font-weight-medium">Students Grade Summary</h1>
          <div className="flex items-end justify-end" style={{ gap: 10 }}>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search students..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportStudents}
              disabled={isExportingStudents}
            >
              <Download className="h-4 w-4 mr-2" />
              {isExportingStudents ? "Exporting..." : "Export Students"}
            </Button>
          </div>
        </div>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Assigned Projects</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoadingStudents || isLoadingContexts || isLoadingProjects ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-12">
                    <div className="flex items-center justify-center">
                      <div className="text-center space-y-2">
                        <div className="animate-spin h-8 w-8 border-4 border-bits-blue border-t-transparent rounded-full mx-auto"></div>
                        <p className="text-muted-foreground">
                          Loading gradebook data...
                        </p>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : studentsError ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-12">
                    <div className="flex items-center justify-center">
                      <div className="text-center space-y-2">
                        <p className="text-red-600">
                          Failed to load gradebook data
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {studentsError?.message || "An error occurred"}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : students.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No students found
                  </TableCell>
                </TableRow>
              ) : (
                students.map((student: StudentGradebookItem) => (
                  <TableRow key={student.user_id}>
                    <TableCell>
                      <div className="font-medium">{student.name}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-muted-foreground">
                        {student.email}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className="font-medium bg-bits-light-grey rounded-lg"
                      >
                        {student.assigned_projects_count}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-left">
                      <Button
                        size="sm"
                        variant="outline"
                        className="hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300 transition-colors"
                        onClick={() => handleViewProfile(student)}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View Profile
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* New Pagination - Similar to UserManagementPage */}
          {!isLoadingStudents && !studentsError && students.length > 0 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages} • {totalItems} items
              </div>

              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="h-9"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Prev
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="h-9"
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>

                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={(value) => {
                    setItemsPerPage(Number(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-[120px] h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 page</SelectItem>
                    <SelectItem value="20">20 page</SelectItem>
                    <SelectItem value="50">50 page</SelectItem>
                    <SelectItem value="100">100 page</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedStudent && (
        <StudentGradebookModal
          open={showStudentModal}
          onOpenChange={handleStudentModalClose}
          userId={selectedStudent.id}
          userName={selectedStudent.name}
          selectedCourse={selectedCourse}
          selectedProject={selectedProject}
        />
      )}
    </div>
  );
}
export default function GradebookPage() {
  const { user } = useAuth();
  const { goBack } = useNavigation();
  const navigate = useNavigate();
  const [isExportingGrades, setIsExportingGrades] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<string>("all");
  const [selectedProject, setSelectedProject] = useState<string>("all");

  // Fetch courses for admin/instructor filter
  const { data: contextsData, error: contextsError } = useQuery({
    queryKey: ["gradeContexts"],
    queryFn: getGradeContexts,
    enabled: user?.role !== ROLES.STUDENT,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch projects for admin/instructor filter
  const { data: projectsData } = useQuery({
    queryKey: ["gradeProjects", selectedCourse],
    queryFn: () => {
      const params: any = {};
      if (selectedCourse && selectedCourse !== "all")
        params.course = selectedCourse;
      return getGradeProjects(params);
    },
    enabled: user?.role !== ROLES.STUDENT,
  });

  // Export handler for grades (admin/instructor only)
  const handleExportGrades = async () => {
    setIsExportingGrades(true);
    try {
      const params: any = { isExport: true };
      if (selectedCourse && selectedCourse !== "all")
        params.courseId = selectedCourse;
      if (selectedProject && selectedProject !== "all")
        params.projectId = selectedProject;

      const exportData = await getAllGrades(params);

      const timestamp = new Date().toISOString().split("T")[0];
      await downloadAsXlsx(exportData, `platform-grades-${timestamp}.xlsx`);
      toast.success(`Grades exported successfully!`);
      // if (exportData.isSuccess && exportData.data?.grades) {
      //   const transformedData = transformGradesForExport(
      //     exportData.data.grades
      //   );
      //   const timestamp = new Date().toISOString().split("T")[0];
      //   downloadExcel(transformedData, `platform-grades-${timestamp}.csv`);
      //   toast.success(
      //     `${transformedData.length} grades exported successfully!`
      //   );
      // } else {
      //   toast.error("No data available to export");
      // }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export grades. Please try again.");
    } finally {
      setIsExportingGrades(false);
    }
  };

  const handleCourseChange = (value: string) => {
    setSelectedProject("all");
    setSelectedCourse(value);
  };

  return (
    <div className="p-6 space-y-6">
      {" "}
      {user?.role === ROLES.STUDENT ? (
        <ProjectHeader
          title="My Grades"
          subtitle="View your academic performance and project grades"
          onBackClick={() => navigate(paths.root)}
        />
      ) : (
        <PageHeader
          title="Platform Gradebook"
          subtitle={
            user?.role === ROLES.INSTRUCTOR
              ? "Manage student grades and track class performance"
              : "Overview of all student grades across the platform"
          }
        >
          <div className="flex items-center space-x-2">
            <Select value={selectedCourse} onValueChange={handleCourseChange}>
              <SelectTrigger className="w-40 bg-white overflow-hidden">
                <SelectValue placeholder="All courses" />
              </SelectTrigger>
              <SelectContent className="max-h-80 overflow-y-auto">
                <SelectItem value="all">All courses</SelectItem>
                {contextsData?.data?.map((context: GradeContext) => (
                  <SelectItem key={context.id} value={context.id}>
                    {context.name} ({context.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="w-40 bg-white overflow-hidden">
                <SelectValue placeholder="All projects" />
              </SelectTrigger>
              <SelectContent className="max-h-80 overflow-y-auto">
                <SelectItem value="all">All projects</SelectItem>
                {projectsData?.data?.map((project: GradeProject) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportGrades}
              disabled={isExportingGrades}
            >
              <Download className="h-4 w-4 mr-2" />
              {isExportingGrades ? "Exporting..." : "Export Grades"}
            </Button>
          </div>
        </PageHeader>
      )}
      {user?.role === ROLES.STUDENT && <StudentGradebookView />}
      {user?.role === ROLES.ADMIN && (
        <AdminGradebookView
          selectedCourse={selectedCourse}
          selectedProject={selectedProject}
        />
      )}
    </div>
  );
}
